import { inject, Injectable, signal, Signal } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { SessionService } from '../session/session.service';
import { StoreService } from '../store/store.service';

/** HostOpenMessage type is sent holds information from the parent site */
export interface HostOpenMessage {
  sessionId: string;
}

/**
 * Service for sending postMessages to parent window.
 * @note For security reasons (not allowing frontned clients to know sdk response)
 *       all former messages delegate to `close()`
 */
@Injectable({
  providedIn: 'root',
})
export class MessageService {
  // Injected dependencies
  private sessionService = inject(SessionService);
  private store = inject(StoreService);

  /** Target origin for sending messages */
  private targetOrigin = environment.targetOrigin;

  /** Allowed origins to receive messages. Loaded from environment */
  private allowedOrigins: string[] = environment.allowedOrigins || [];

  /** Signal for incoming host-open messages */
  public hostOpen = signal<{ sessionId: string } | null>(null);

  /** Whether a `transend:ready` message has been sent to the parent */
  private isReady = false;

  constructor() {
    this.setupMessageListener();
  }

  /**
   * Optionally set a stricter target origin
   * @param origin The target origin to use for postMessage
   */
  public setTargetOrigin(origin: string): void {
    this.targetOrigin = origin;
  }

  /**
   * Notify parent that iframe is ready
   *
   * @param extra Optional additional data to include in the message
   */
  ready(extra?: Record<string, unknown>) {
    this.post('transend:ready', { ts: Date.now(), ...(extra || {}) });
    this.isReady = true;
  }

  /**
   * Notify parent that iframe size changed
   *
   * @param width New width of the iframe
   * @param height New height of the iframe
   */
  resize(width: number, height: number) {
    this.post('transend:resize', { width, height });
  }

  /**
   * Close the modal (leave the session as is)
   */
  onlyCloseModal(info?: Record<string, unknown>) {
    this.post('transend:close-request', { ts: Date.now(), ...(info || {}) });
  }

  /**
   * Close the session and shut the modal
   *
   * @param info Optional additional data to include in the message
   */
  closeModalAndSession(info?: Record<string, unknown>) {
    const sessionId = this.store.sessionId();
    if (!sessionId || sessionId.trim() === '') {
      throw new Error('session id must be defined');
    }

    this.sessionService.closeModalAndSession(sessionId).subscribe({
      next: () => this.post('transend:close-request', { ts: Date.now(), ...(info || {}) }),
      error: (_) => this.post('transend:close-request', { ts: Date.now(), ...(info || {}) }),
    });
  }

  /**
   * Set up listener for incoming postMessages from parent window
   */
  private setupMessageListener(): void {
    if (typeof window === 'undefined') return;

    window.addEventListener('message', (event: MessageEvent) => {
      // Validate origin before processing any message
      if (!this.isOriginAllowed(event.origin)) {
        console.warn('[MESSAGE_SERVICE]: Rejected message from unauthorized origin:', event.origin);
        return;
      }

      // Validate message structure
      if (!event.data || typeof event.data !== 'object') return;
      if (!event.data.type || typeof event.data.type !== 'string') return;
      if (event.data.type.indexOf('transend:') !== 0) return;

      // Handle different message types
      switch (event.data.type) {
        case 'transend:host-open':
          if (event.data.payload && event.data.payload.sessionId) {
            this.hostOpen.set({
              sessionId: event.data.payload.sessionId,
            });
          }
          break;
        case 'transend:init':
          if (this.isReady) {
            this.post('transend:init-ack', event.data.payload);
          }
          break;
        default:
          // Ignore other message types
          break;
      }
    });
  }

  /**
   * Check if the origin is in the allowed list
   * @param origin The origin to validate
   * @returns True if origin is allowed
   */
  private isOriginAllowed(origin: string): boolean {
    return this.allowedOrigins.includes(origin) || this.allowedOrigins.includes('*');
  }

  /**
   * Return status on if the app is inside an iframe
   * @returns True if app is running inside an iframe
   */
  private get isFramed(): boolean {
    try {
      return typeof window !== 'undefined' && window.parent && window.parent !== window;
    } catch {
      return false;
    }
  }

  /**
   * Low-level post helper
   * @param type Message type to send through the SDK to the parent
   * @param payload Optional message payload
   * @param error Optional message error
   */
  private post(
    type: 'transend:ready' | 'transend:resize' | 'transend:close-request' | 'transend:init-ack',
    payload?: unknown,
    error?: unknown,
  ): void {
    if (typeof window === 'undefined') return;

    const target = this.isFramed ? window.parent : window;
    try {
      target.postMessage({ type, ...(error ? { error } : {}), ...(payload ? { payload } : {}) }, this.targetOrigin);
    } catch (_) {
      /* no-op */
    }
  }
}
